import React, { useEffect, useState } from 'react'
import { ScrollView, View, Linking, Platform, TouchableOpacity } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Screen, Header, Text, Icon, Button } from '@components'
import { useLanguage } from '@context'
import { useScreen, useSubscription } from '@hooks'
import { showInfoToast, showSuccessToast, showErrorToast, fromApiDateFormat } from '@utils'
import { purchaseManager } from '../../../utils/purchases/purchaseManager'
import { TierCard } from './components/TierCard'
import { getSubscriptionStyles } from './style'

// After a successful web purchase, the Premium entitlement is granted
// server-side by the RevenueCat webhook, which is asynchronous. We poll the
// subscription query a few times until `isPremium` flips before declaring
// success. ~6 × 3s ≈ 18s covers the typical webhook round-trip.
const ENTITLEMENT_POLL_ATTEMPTS = 6
const ENTITLEMENT_POLL_INTERVAL_MS = 3000

// Below this viewport width the two tiers stack; at/above they sit side by side.
const SIDE_BY_SIDE_MIN_WIDTH = 768

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

const FREE_FEATURE_KEYS = [
  'subscription.freeFeatureContacts',
  'subscription.freeFeatureGhosts',
  'subscription.freeFeatureNotes',
  'subscription.freeFeatureReminders',
  'subscription.freeFeatureMap',
  'subscription.freeFeatureNoStats',
]

const INFINITY_FEATURE_KEYS = [
  'subscription.infinityFeatureUnlimited',
  'subscription.infinityFeatureGhosts',
  'subscription.infinityFeatureAI',
  'subscription.infinityFeatureIntegrations',
  'subscription.infinityFeatureMap',
  'subscription.infinityFeatureForecast',
]

// Legal links shown on the paywall (App Store guideline 3.1.2). Terms of Use =
// Apple's standard EULA (always valid); privacy = our hosted policy.
const TERMS_OF_USE_URL = 'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/'
const PRIVACY_POLICY_URL = 'https://keeptouch.co/privacy'

export const Subscription: React.FC = () => {
  const theme = useTheme()
  const styles = getSubscriptionStyles(theme)
  const navigation = useNavigation()
  const { isWeb, width } = useScreen()
  const { t } = useLanguage()
  const { isPremium, expiresAt, refetch } = useSubscription()

  const [priceLabel, setPriceLabel] = useState<string | null>(null)
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [isActivating, setIsActivating] = useState(false)
  const [isRestoring, setIsRestoring] = useState(false)

  // The native stores expose a self-serve subscription management page; the web
  // (Stripe/RevenueCat) has no equivalent deep link, so we hide the button there.
  const manageStoreUrl =
    Platform.OS === 'ios'
      ? 'https://apps.apple.com/account/subscriptions'
      : Platform.OS === 'android'
        ? 'https://play.google.com/store/account/subscriptions'
        : null

  const canPurchase = purchaseManager.isAvailable
  const isSideBySide = width >= SIDE_BY_SIDE_MIN_WIDTH

  const premiumMetaText = expiresAt
    ? t('subscription.expiresOn', { date: fromApiDateFormat(expiresAt, 'DD/MM/YYYY') })
    : t('subscription.premiumActive')

  // Prefetch the offering so the Infinity price reflects the real store price
  // (web only). Missing key / offering degrades to the fallback label — never
  // throws.
  useEffect(() => {
    if (!canPurchase) {
      return
    }
    let isActive = true
    purchaseManager
      .getOffering()
      .then((offering) => {
        if (isActive && offering) {
          setPriceLabel(offering.priceLabel)
        }
      })
      .catch(() => undefined)
    return () => {
      isActive = false
    }
  }, [canPurchase])

  // Poll the subscription query until the webhook has granted the entitlement.
  const waitForEntitlement = async (): Promise<void> => {
    for (let attempt = 0; attempt < ENTITLEMENT_POLL_ATTEMPTS; attempt += 1) {
      const result = await refetch()
      if (result.data?.isPremium) {
        return
      }
      if (attempt < ENTITLEMENT_POLL_ATTEMPTS - 1) {
        await wait(ENTITLEMENT_POLL_INTERVAL_MS)
      }
    }
  }

  const handleUpgrade = async (): Promise<void> => {
    // Native (Phase 3b) keeps the stub → isAvailable is false, so we only tell
    // the user it is coming. On web the purchase manager is wired for real.
    if (!canPurchase) {
      showInfoToast(t('subscription.comingSoon'), t('subscription.comingSoonMessage'))
      return
    }

    setIsPurchasing(true)
    try {
      const result = await purchaseManager.purchasePremium()
      if (result.ok) {
        setIsActivating(true)
        await waitForEntitlement()
        showSuccessToast(
          t('subscription.purchaseSuccessTitle'),
          t('subscription.purchaseSuccessMessage'),
        )
      } else if (result.reason !== 'cancelled') {
        // A user cancellation is not an error — return silently. Anything else
        // surfaces a translated error toast.
        showErrorToast(t('subscription.purchaseErrorTitle'), t('subscription.purchaseErrorMessage'))
      }
    } finally {
      setIsActivating(false)
      setIsPurchasing(false)
    }
  }

  const handleRestore = async (): Promise<void> => {
    setIsRestoring(true)
    try {
      await purchaseManager.restorePurchases()
      const result = await refetch()
      if (result.data?.isPremium) {
        showSuccessToast(
          t('subscription.restoreSuccessTitle'),
          t('subscription.restoreSuccessMessage'),
        )
      } else {
        showInfoToast(t('subscription.restoreNoneTitle'), t('subscription.restoreNoneMessage'))
      }
    } catch {
      showErrorToast(t('subscription.restoreErrorTitle'), t('subscription.restoreErrorMessage'))
    } finally {
      setIsRestoring(false)
    }
  }

  const handleManage = (): void => {
    if (!manageStoreUrl) {
      return
    }
    Linking.openURL(manageStoreUrl).catch(() => {
      showErrorToast(t('subscription.manageErrorTitle'), t('subscription.manageErrorMessage'))
    })
  }

  const infinityPrice = priceLabel
    ? t('subscription.infinityPricePerMonth', { price: priceLabel })
    : t('subscription.infinityPriceFallback')

  const ctaLabel = isActivating
    ? t('subscription.activating')
    : isPurchasing
      ? t('subscription.processing')
      : t('subscription.infinityCta')

  return (
    <Screen>
      <Header
        title={t('subscription.title')}
        iconLeft={isWeb ? undefined : 'arrowBack'}
        onPressLeft={() => navigation.goBack()}
      />

      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {isPremium ? (
          <View style={styles.premiumCard} testID="subscription-premium-state">
            <Icon name="star" size={theme.iconSize.jumbo} color={theme.colors.gold} />
            <Text type="titleBold" style={styles.premiumTitle}>
              {t('subscription.premiumTitle')}
            </Text>
            <Text type="body" style={styles.premiumSubtitle}>
              {t('subscription.premiumSubtitle')}
            </Text>
            <Text type="bodySmall" style={styles.premiumMeta}>
              {premiumMetaText}
            </Text>
            {manageStoreUrl ? (
              <Button
                testID="subscription-manage-cta"
                title={t('subscription.manageCta')}
                textColor={theme.colors.gold}
                style={styles.manageButton}
                onPress={handleManage}
              />
            ) : null}
          </View>
        ) : (
          <>
            <View style={styles.hero}>
              <Text type="titleBigBold" center>
                {t('subscription.headline')}
              </Text>
              <Text type="body" style={styles.heroIntro}>
                {t('subscription.intro')}
              </Text>
            </View>

            <View style={[styles.tiers, isSideBySide && styles.tiersRow]}>
              <View style={isSideBySide ? styles.tierColumn : undefined}>
                <TierCard
                  testID="subscription-tier-free"
                  title={t('subscription.freeTierTitle')}
                  subtitle={t('subscription.freeTierSubtitle')}
                  price={t('subscription.freePriceLabel')}
                  features={FREE_FEATURE_KEYS.map((key) => t(key))}
                />
              </View>

              <View style={isSideBySide ? styles.tierColumn : undefined}>
                <TierCard
                  testID="subscription-tier-infinity"
                  highlighted
                  badgeLabel={t('subscription.infinityBadge')}
                  title={t('subscription.infinityTierTitle')}
                  subtitle={t('subscription.infinityTierSubtitle')}
                  price={infinityPrice}
                  features={INFINITY_FEATURE_KEYS.map((key) => t(key))}
                >
                  <Button
                    testID="subscription-upgrade-cta"
                    title={ctaLabel}
                    iconName="rocket"
                    iconColor={theme.colors.white}
                    textColor={theme.colors.white}
                    style={styles.ctaButton}
                    disabled={isPurchasing}
                    onPress={handleUpgrade}
                  />
                </TierCard>
              </View>
            </View>
          </>
        )}

        <Button
          testID="subscription-restore"
          title={isRestoring ? t('subscription.restoring') : t('subscription.restoreCta')}
          textColor={theme.colors.secondary}
          style={styles.restoreButton}
          disabled={isRestoring}
          onPress={handleRestore}
        />

        <View style={styles.legalFooter}>
          <Text type="bodySmall" style={styles.legalText}>
            {t('subscription.legalDisclosure')}
          </Text>
          <View style={styles.legalLinks}>
            <TouchableOpacity
              testID="subscription-terms-link"
              onPress={() => Linking.openURL(TERMS_OF_USE_URL)}
            >
              <Text type="bodySmall" style={styles.legalLink}>
                {t('subscription.termsOfUse')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              testID="subscription-privacy-link"
              onPress={() => Linking.openURL(PRIVACY_POLICY_URL)}
            >
              <Text type="bodySmall" style={styles.legalLink}>
                {t('subscription.privacyPolicy')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </Screen>
  )
}
